import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useGetTodoQuery,
  useClaimTodoMutation,
  useCompleteTodoMutation,
  useReleaseTodoMutation,
  useDeleteTodoMutation,
} from '../../redux/api/todoApi';
import { useApiErrorToast } from '../../hooks/useApiError';
import FormatDateTime from '../../components/dateTime/FormatDateTime';

const STATUS_LABEL = {
  OPEN: 'offen',
  IN_PROGRESS: 'in Arbeit',
  DONE: 'erledigt',
  CANCELLED: 'abgebrochen',
};

const PRIORITY_LABEL = { LOW: 'niedrig', NORMAL: 'normal', HIGH: 'hoch' };

const TodoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();
  const me = useSelector((state) => state.auth.username);
  const isSuperUser = useSelector((state) => state.auth.isSuperUser);

  const { data: todo, isLoading } = useGetTodoQuery(id);
  const [claim] = useClaimTodoMutation();
  const [complete] = useCompleteTodoMutation();
  const [release] = useReleaseTodoMutation();
  const [destroy] = useDeleteTodoMutation();

  if (isLoading) return <p>Wird geladen…</p>;
  if (!todo) return <div className='alert alert-warning'>Nicht gefunden.</div>;

  const isMine = todo.assigned_to_username === me;

  const safeCall = async (fn, success, failureFallback) => {
    try {
      await fn().unwrap();
      toast.success(success);
    } catch (err) {
      apiErrorToast(err, failureFallback);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Aufgabe wirklich löschen?')) return;
    try {
      await destroy(id).unwrap();
      toast.success('Aufgabe gelöscht');
      navigate('/todo');
    } catch (err) {
      apiErrorToast(err, 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className='col-md-8 mx-auto'>
      <div className='d-flex justify-content-between align-items-start mb-3'>
        <div>
          <span
            className='badge me-2'
            style={{ backgroundColor: todo.category?.color }}
          >
            {todo.category?.icon && (
              <i className={`fas fa-${todo.category.icon} me-1`} />
            )}
            {todo.category?.name}
          </span>
          <span className='badge bg-light text-dark me-2'>
            {STATUS_LABEL[todo.status]}
          </span>
          <span className='badge bg-secondary me-2'>
            Priorität: {PRIORITY_LABEL[todo.priority]}
          </span>
          {todo.is_overdue && (
            <span className='badge bg-danger'>überfällig</span>
          )}
        </div>
        <button className='btn btn-sm btn-link' onClick={() => navigate('/todo')}>
          ← zurück
        </button>
      </div>

      <h3>{todo.title}</h3>
      {todo.description && (
        <p className='text-muted' style={{ whiteSpace: 'pre-wrap' }}>
          {todo.description}
        </p>
      )}

      <table className='table table-sm mt-3'>
        <tbody>
          <tr>
            <th>angelegt</th>
            <td>
              <FormatDateTime dateTime={todo.created_at} /> von{' '}
              <b>{todo.created_by_username}</b>
            </td>
          </tr>
          {todo.due_at && (
            <tr>
              <th>fällig</th>
              <td>
                <FormatDateTime dateTime={todo.due_at} />
              </td>
            </tr>
          )}
          <tr>
            <th>zugewiesen</th>
            <td>{todo.assigned_to_username || <em>niemand</em>}</td>
          </tr>
          {todo.completed_at && (
            <tr>
              <th>erledigt</th>
              <td>
                <FormatDateTime dateTime={todo.completed_at} /> von{' '}
                <b>{todo.completed_by_username}</b>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className='mt-3'>
        {todo.status !== 'DONE' &&
          (todo.status === 'OPEN' || !todo.assigned_to_username) && (
            <button
              className='btn btn-primary me-2'
              onClick={() =>
                safeCall(
                  () => claim(id),
                  'Aufgabe übernommen',
                  'Übernehmen fehlgeschlagen'
                )
              }
            >
              <i className='fas fa-hand-paper me-2' />
              Übernehmen
            </button>
          )}
        {todo.status === 'IN_PROGRESS' && isMine && (
          <>
            <button
              className='btn btn-success me-2'
              onClick={() =>
                safeCall(
                  () => complete(id),
                  'Aufgabe erledigt',
                  'Erledigen fehlgeschlagen'
                )
              }
            >
              <i className='fas fa-check me-2' />
              Erledigt
            </button>
            <button
              className='btn btn-outline-secondary me-2'
              onClick={() =>
                safeCall(
                  () => release(id),
                  'Aufgabe freigegeben',
                  'Freigeben fehlgeschlagen'
                )
              }
            >
              Freigeben
            </button>
          </>
        )}
        {isSuperUser && (
          <button className='btn btn-outline-danger ms-auto' onClick={handleDelete}>
            <i className='fas fa-trash me-2' />
            Löschen
          </button>
        )}
      </div>
    </div>
  );
};

export default TodoDetail;
